import { useEffect, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  Box,
  TextField,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Link,
  useTheme,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import PlaceIcon from '@mui/icons-material/Place';
import { NO_AUTOFILL, keepNoAutofillOnShadowInput } from '../../utils/noAutofill';
import { ensureRoomBelow } from '../../utils/ensureRoomBelow';
import {
  noteSuggestionInput,
  noteSuggestionSelected,
  noteSuggestionKey,
  noteSuggestionClosed,
} from '../../utils/placeSuggestionState';
import { parseAddressComponents } from '../../utils/addressComponents';
import { buildSubDestinationOptions } from '../../utils/subDestinations';
import { geocodePreciseAddress, hasCoordinates } from '../../utils/addressGeocoding';
import MapPinDialog from './MapPinDialog';

// Nach dem Verlassen kurz warten: Eine per Enter übernommene Auswahl kommt
// asynchron (fetchFields) und soll vor der Prüfung ankommen.
const RESOLVE_ON_BLUR_DELAY_MS = 300;

// Monkey-Patch: Google's <gmp-place-autocomplete> verwendet einen CLOSED
// Shadow DOM, der nachträglichen Zugriff auf den internen <input> blockiert.
// Wir intercepten attachShadow global einmalig, um für genau dieses Element
// den Shadow DOM als OPEN zu erzwingen – damit können wir nach der Auswahl
// einer Adresse ohne Hausnummer den Cursor gezielt positionieren, sodass der
// Disponent die Hausnummer direkt weiterschreiben kann.
// Basiert auf: https://juancrg90.me/posts/mastering-google-places-new-api/
if (typeof window !== 'undefined' && !window.__gmpShadowOpenPatched) {
  const originalAttachShadow = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function (init) {
    if (this.localName === 'gmp-place-autocomplete') {
      return originalAttachShadow.call(this, { ...init, mode: 'open' });
    }
    return originalAttachShadow.call(this, init);
  };
  window.__gmpShadowOpenPatched = true;
}

/**
 * Setzt den Cursor an die Hausnummer-Position, sobald der neue Wert im internen
 * <input> angekommen ist.
 *
 * Bewusst kein fester Timeout: Ein zu früh eintreffendes Enter der
 * Tastaturführung würde den Fokus weiterschieben, und die verzögerte
 * Positionierung holte ihn danach wieder zurück – der Disponent tippt die
 * Hausnummer dann in ein Feld, das er längst verlassen hat. Solange
 * `data-nav-cursor-pending` gesetzt ist, verweigert useFormKeyboardNav jeden
 * Fokuswechsel (siehe Konzept 3.5).
 */
const CURSOR_PLACEMENT_TIMEOUT_MS = 500;

const placeCursor = (element, expectedValue, position) => {
  element.dataset.navCursorPending = 'true';
  const startedAt = performance.now();

  const done = () => delete element.dataset.navCursorPending;

  const tick = () => {
    try {
      const internalInput = element.shadowRoot?.querySelector('input');
      if (internalInput && internalInput.value === expectedValue) {
        internalInput.focus();
        internalInput.setSelectionRange(position, position);
        done();
        return;
      }
      if (performance.now() - startedAt > CURSOR_PLACEMENT_TIMEOUT_MS) {
        done();
        return;
      }
      requestAnimationFrame(tick);
    } catch (cursorError) {
      // Wenn Google die interne Struktur ändert, Feature still deaktivieren –
      // die Adressauswahl selbst funktioniert weiter.
      console.warn('Cursor-Positionierung nach Adressauswahl nicht möglich:', cursorError);
      done();
    }
  };

  requestAnimationFrame(tick);
};

const SUB_DESTINATION_HEADINGS = {
  pickup: 'Abholort genauer auswählen',
  destination: 'Ziel genauer auswählen',
  stop: 'Halt genauer auswählen',
};

const subDestinationIcon = (label) => {
  const l = label.toLowerCase();
  if (l.includes('abflug')) return <FlightTakeoffIcon fontSize="small" />;
  if (l.includes('ankunft')) return <FlightLandIcon fontSize="small" />;
  if (/^p\d|parkhaus|parkplatz/.test(l)) return <LocalParkingIcon fontSize="small" />;
  return <PlaceIcon fontSize="small" />;
};

const PlaceAutocompleteElementWrapper = ({
  onPlaceSelect, 
  label, 
  value = '', 
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = true,
  placeholder = '',
  // Lokaler Places-Bias aus dem Betriebsgebiet (CircleLiteral | BoundsLiteral)
  locationBias = null,
  // Name für die Tastaturführung (useFormKeyboardNav, siehe
  // docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md 3.4)
  navName = null,
  // State wird von übergeordneter Komponente verwaltet
  hasPrefilledValue = false,
  isEditingMode = false,
  onEditingModeActivation,
  // 'pickup' | 'destination' | 'stop': Bei Orten mit Unterzielen (Terminals
  // eines Flughafens) erst ein Unterziel wählen lassen
  // (nextcab_mobile/docs/general/adressauswahl_konzept.md §2.2). Nur in
  // Formularen, aus denen Fahrten entstehen — nicht bei Festpreisen (dort
  // bestimmt der Punkt die Zone).
  subDestinationRole = null,
  // Vorschlagspflicht (adressauswahl_konzept.md §3): Text ohne Koordinate
  // (`location`) wird beim Verlassen nur übernommen, wenn Google eine genaue
  // Hausadresse findet; sonst Hinweis + „Auf Karte festlegen".
  requireLocation = false,
  location = null,
  geocodeBounds = null,
  ...textFieldProps 
}) => {
  const [placeAutocompleteElement, setPlaceAutocompleteElement] = useState(null);
  const theme = useTheme();
  const colorMode = theme.palette.mode;
  const colorModeRef = useRef(colorMode);
  colorModeRef.current = colorMode;
  const [fallbackMode, setFallbackMode] = useState(false);
  const [apiError, setApiError] = useState(null);
  // Offene Unterziel-Auswahl: { mainName, options, components }
  const [subChoice, setSubChoice] = useState(null);
  const [unresolvedHint, setUnresolvedHint] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const needsLocation = requireLocation && !!value?.trim() && !hasCoordinates(location);
  // Aktuelle Werte für die verzögerte Prüfung nach dem Verlassen des Feldes.
  const latestRef = useRef({});
  latestRef.current = { value, needsLocation, subChoiceOpen: !!subChoice, onPlaceSelect };
  const containerRef = useRef(null);
  const noAutofillCleanupRef = useRef(null);
  const places = useMapsLibrary('places');

  // State wird jetzt von übergeordneter Komponente verwaltet - entferne lokalen useEffect

  // Erzeugt das Google-Element. Es wird in einer lokalen Variable gehalten,
  // damit das Aufräumen genau dieses Element entfernt: Vorher prüfte der
  // Aufräumteil den (in der Closure veralteten) State und ließ das Element
  // stehen, und der nächste Durchlauf brach wegen `placeAutocompleteElement`
  // in seiner Closure ab. Übrig blieb ein Element im DOM ohne Listener – die
  // Eingabe erreichte `onChange` nie (bei nachträglich hinzugefügten
  // Zwischenstopps reproduzierbar).
  useEffect(() => {
    if (!places || !containerRef.current || disabled || hasPrefilledValue) return undefined;
    const container = containerRef.current;
    let autocompleteElement = null;

    try {
      // Prüfe ob PlaceAutocompleteElement verfügbar ist (beta/alpha channel)
      if (!places.PlaceAutocompleteElement) {
        console.warn('PlaceAutocompleteElement nicht verfügbar, verwende Fallback-Modus');
        setFallbackMode(true);
        return;
      }

      // Stelle sicher, dass der Container leer ist
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      const options = {
        requestedLanguage: 'de',
        requestedRegion: 'DE',
      };

      // Lokale Treffer aus dem Betriebsgebiet bevorzugen (weicher Bias).
      if (locationBias) {
        options.locationBias = locationBias;
      }

      autocompleteElement = new places.PlaceAutocompleteElement(options);
      
      
      // Minimales Styling - lass Google Maps das meiste selbst handhaben
      autocompleteElement.style.width = '100%';
      autocompleteElement.style.display = 'block';
      // Google folgt sonst dem System-Farbschema (`light dark`). Fest auf den
      // Nachtmodus des Portals setzen, sonst erschienen die Felder bei
      // „Hell" + dunklem Betriebssystem schwarz (und umgekehrt).
      autocompleteElement.style.colorScheme = colorModeRef.current;
      
      if (placeholder) {
        autocompleteElement.placeholder = placeholder;
      }

      // Browser-Autofill unterdrücken: Chromes Vorschläge legen sich sonst
      // über die Places-Vorschlagsliste. Google setzt seine Attribute nach dem
      // Aufbau erneut, deshalb überwachen statt einmalig setzen.
      noAutofillCleanupRef.current?.();
      noAutofillCleanupRef.current = keepNoAutofillOnShadowInput(
        autocompleteElement,
        navName ? `${navName}-${Math.random().toString(36).slice(2, 10)}` : NO_AUTOFILL
      );

      // Markierung für die Tastaturführung. Sitzt direkt auf dem Host, damit
      // useFormKeyboardNav das Feld über composedPath() wiedererkennt.
      if (navName) {
        autocompleteElement.dataset.nav = navName;
        autocompleteElement.dataset.navType = 'place';
      }
      
      // Setze den aktuellen Wert wenn vorhanden
      if (value) {
        autocompleteElement.value = value;
      }

      // Event-Listener werden in separatem useEffect hinzugefügt

      // Element zum Container hinzufügen
      container.appendChild(autocompleteElement);
      setPlaceAutocompleteElement(autocompleteElement);
      setApiError(null);

    } catch (error) {
      console.error('Fehler beim Initialisieren von PlaceAutocompleteElement:', error);
      setFallbackMode(true);
      setApiError('Places API nicht verfügbar');
    }

    return () => {
      noAutofillCleanupRef.current?.();
      noAutofillCleanupRef.current = null;
      if (autocompleteElement && container.contains(autocompleteElement)) {
        try {
          container.removeChild(autocompleteElement);
        } catch (error) {
          // Element bereits entfernt
        }
      }
      setPlaceAutocompleteElement(current => (current === autocompleteElement ? null : current));
    };
    // `value`, `placeholder`, `navName`, `locationBias` bewusst nicht: Sie
    // werden in eigenen Effects nachgeführt, ein Neuaufbau verlöre den Fokus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, disabled, hasPrefilledValue]);

  // Feld hat (wieder) eine Koordinate → Hinweis weg.
  useEffect(() => {
    if (!needsLocation) setUnresolvedHint(false);
  }, [needsLocation]);

  // Synchronisiere den Wert mit dem Element
  useEffect(() => {
    if (placeAutocompleteElement && value !== undefined) {
      // Nur setzen wenn der Wert sich unterscheidet, um Endlos-Schleifen zu vermeiden
      if (placeAutocompleteElement.value !== value) {
        placeAutocompleteElement.value = value;
      }
    }
  }, [value, placeAutocompleteElement]);

  // Nachtmodus umgeschaltet: bestehendes Google-Element mitziehen
  useEffect(() => {
    if (placeAutocompleteElement) placeAutocompleteElement.style.colorScheme = colorMode;
  }, [placeAutocompleteElement, colorMode]);

  // Bias nachträglich aktualisieren, falls das Betriebsgebiet erst nach dem
  // Erstellen des Elements aus den Einstellungen geladen wurde.
  useEffect(() => {
    if (!placeAutocompleteElement || !locationBias) return;
    try {
      placeAutocompleteElement.locationBias = locationBias;
    } catch (error) {
      // Ältere/abweichende API-Versionen erlauben das Setzen evtl. nicht –
      // die Suche funktioniert dann ohne lokalen Bias weiter.
      console.warn('locationBias konnte nicht aktualisiert werden:', error);
    }
  }, [placeAutocompleteElement, locationBias]);

  // Separate useEffect für Event-Listener Updates
  useEffect(() => {
    if (!placeAutocompleteElement) return;

    const handleGmpSelect = async (event) => {
      try {
        const place = event.placePrediction.toPlace();

        await place.fetchFields({
          // addressComponents: für Formulare, die die Anschrift zerlegt
          // brauchen (z. B. Rechnungskunden nach § 14 UStG).
          // subDestinations liegt wie displayName in der Pro-Stufe.
          fields: [
            'displayName', 'formattedAddress', 'location', 'types', 'addressComponents',
            ...(subDestinationRole ? ['subDestinations'] : []),
          ]
        });

        // Ort mit Unterzielen: Der Hauptpunkt liegt auf dem Gelände, also
        // erst Terminal/Eingang wählen lassen. Übernommen wird erst die Wahl.
        const subPlaces = subDestinationRole && Array.isArray(place.subDestinations)
          ? place.subDestinations
          : [];
        if (subPlaces.length > 0) {
          const loaded = await Promise.all(subPlaces.map(async (subPlace) => {
            try {
              await subPlace.fetchFields({ fields: ['displayName', 'location'] });
              return subPlace;
            } catch (subError) {
              console.warn('Unterziel konnte nicht geladen werden:', subError);
              return null;
            }
          }));
          const mainName = place.displayName || '';
          const options = buildSubDestinationOptions(loaded, mainName, subDestinationRole);
          if (options.length > 0) {
            setSubChoice({
              mainName,
              options,
              components: parseAddressComponents(place.addressComponents),
            });
            return;
          }
        }

        const address = place.formattedAddress || '';
        const name = place.displayName || '';
        // Wenn ein Ortsname vorhanden ist und nicht bereits in der Adresse enthalten, voranstellen
        let formattedAddress = name && address && !address.startsWith(name)
          ? `${name}, ${address}`
          : address || name;
        const location = {
          latitude: place.location.lat(),
          longitude: place.location.lng()
        };

        // Adressen ohne Hausnummer: Der Place ist eine reine Straße (`route`),
        // aber kein konkretes Gebäude (`street_address` / `premise`). In diesem
        // Fall fügen wir vor dem ersten Komma ein Leerzeichen ein und setzen
        // danach den Cursor zwischen Leerzeichen und Komma – so kann der
        // Disponent die Hausnummer einfach weitertippen, ohne manuell zu klicken.
        const types = Array.isArray(place.types) ? place.types : [];
        const isStreetOnly =
          types.includes('route') &&
          !types.includes('street_address') &&
          !types.includes('premise');

        let targetCursorPos = null;
        if (isStreetOnly) {
          const commaIdx = formattedAddress.indexOf(',');
          if (commaIdx > 0) {
            formattedAddress =
              formattedAddress.slice(0, commaIdx) + ' ' + formattedAddress.slice(commaIdx);
            // Cursor direkt vor das (verschobene) Komma, d. h. hinter das neu
            // eingefügte Leerzeichen: Straßenname + Space + | + Komma + Rest
            targetCursorPos = commaIdx + 1;
          }
        }

        // Auswahl getroffen → Vorschlagsliste ist zu, der Ort ist bestätigt.
        // Ab jetzt darf Enter wieder von der Tastaturführung ausgewertet
        // werden, damit nach der Hausnummer weitergesprungen werden kann.
        noteSuggestionSelected(placeAutocompleteElement.dataset, formattedAddress);

        if (onPlaceSelect) {
          onPlaceSelect(formattedAddress, location, parseAddressComponents(place.addressComponents));
        }

        // Cursor positionieren, sobald React den neuen Wert in das Element
        // synchronisiert hat. Greift auf den internen <input> im (dank Patch
        // offenen) Shadow DOM zu.
        if (targetCursorPos !== null && placeAutocompleteElement) {
          placeCursor(placeAutocompleteElement, formattedAddress, targetCursorPos);
        }
      } catch (error) {
        console.error('Fehler beim Verarbeiten der Ortsauswahl:', error);
        setApiError('Fehler beim Laden der Ortsdaten');
      }
    };

    const handleInput = (event) => {
      // Bei PlaceAutocompleteElement muss der Wert direkt vom Element gelesen werden
      const currentValue = placeAutocompleteElement.value || '';

      noteSuggestionInput(placeAutocompleteElement.dataset, currentValue);

      if (onChange) {
        onChange({ target: { value: currentValue } });
      }
    };

    const handleElementKeyDown = (event) => {
      // Alt+↑/↓ verschiebt im RideForm den Routenpunkt und öffnet keine
      // Vorschlagsliste – sonst hielte die Tastaturführung die Liste für offen
      // und überließe die Taste Google.
      if (event.altKey) return;
      noteSuggestionKey(placeAutocompleteElement.dataset, event.key);
    };

    const handleFocusOut = () => {
      noteSuggestionClosed(placeAutocompleteElement.dataset);
      if (!requireLocation) return;
      setTimeout(async () => {
        const before = latestRef.current;
        if (!before.needsLocation || before.subChoiceOpen) return;
        const text = before.value;
        const precise = await geocodePreciseAddress(text, { bounds: geocodeBounds });
        const now = latestRef.current;
        // Inzwischen weitergetippt oder anders aufgelöst → Ergebnis verwerfen.
        if (now.value !== text || !now.needsLocation) return;
        if (precise) {
          // Text des Disponenten bleibt, nur die Koordinate kommt dazu.
          now.onPlaceSelect?.(text, { latitude: precise.latitude, longitude: precise.longitude });
        } else {
          setUnresolvedHint(true);
        }
      }, RESOLVE_ON_BLUR_DELAY_MS);
    };

    // Beim Betreten des Feldes Platz für die Vorschlagsliste schaffen. Am
    // Element selbst statt in der Tastaturführung, damit es auch bei Tab und
    // Mausklick greift.
    const handleFocusIn = () => {
      ensureRoomBelow(placeAutocompleteElement);
    };

    // Entferne alte Listener falls vorhanden
    placeAutocompleteElement.removeEventListener('input', handleInput);

    // Füge neue Listener hinzu
    placeAutocompleteElement.addEventListener('input', handleInput);
    placeAutocompleteElement.addEventListener('keydown', handleElementKeyDown);
    placeAutocompleteElement.addEventListener('focusin', handleFocusIn);
    placeAutocompleteElement.addEventListener('focusout', handleFocusOut);

    // Event-Listener für Place Selection
    placeAutocompleteElement.addEventListener('gmp-select', handleGmpSelect);

    return () => {
      if (placeAutocompleteElement) {
        placeAutocompleteElement.removeEventListener('gmp-select', handleGmpSelect);
        placeAutocompleteElement.removeEventListener('focusout', handleFocusOut);
        placeAutocompleteElement.removeEventListener('focusin', handleFocusIn);
        placeAutocompleteElement.removeEventListener('keydown', handleElementKeyDown);
        placeAutocompleteElement.removeEventListener('input', handleInput);
      }
    };
  }, [placeAutocompleteElement, onPlaceSelect, onChange, subDestinationRole, requireLocation, geocodeBounds]);

  const confirmMapPin = (pinLocation) => {
    setPinDialogOpen(false);
    if (placeAutocompleteElement) {
      noteSuggestionSelected(placeAutocompleteElement.dataset, value);
    }
    if (onPlaceSelect) onPlaceSelect(value, pinLocation);
  };

  const chooseSubDestination = (option) => {
    const components = subChoice?.components;
    setSubChoice(null);
    if (placeAutocompleteElement) {
      noteSuggestionSelected(placeAutocompleteElement.dataset, option.address);
    }
    if (onPlaceSelect) onPlaceSelect(option.address, option.location, components);
  };

  // Auswahl geschlossen ohne Unterziel → Feld leeren statt den Geländepunkt
  // zu übernehmen.
  const cancelSubDestination = () => {
    setSubChoice(null);
    if (placeAutocompleteElement) {
      placeAutocompleteElement.value = '';
      noteSuggestionInput(placeAutocompleteElement.dataset, '');
    }
    if (onChange) onChange({ target: { value: '' } });
  };

  // Handler wird von übergeordneter Komponente bereitgestellt
  const handleEditingModeActivation = onEditingModeActivation || (() => {});

  // In den TextField-Pfaden gibt es keine Vorschlagsliste – das Feld verhält
  // sich für die Tastaturführung wie ein normales Textfeld.
  const navProps = navName ? { 'data-nav': navName, 'data-nav-type': 'text' } : {};
  const noAutofillProps = { autoComplete: NO_AUTOFILL };

  // Wenn wir eine vorausgefüllte Adresse haben und noch nicht im Bearbeitungsmodus sind,
  // zeige normales TextField
  if (hasPrefilledValue && !isEditingMode) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={onChange}
        onFocus={handleEditingModeActivation}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        placeholder={placeholder || 'Adresse eingeben...'}
        InputProps={{
          style: { cursor: 'pointer' }
        }}
        {...navProps}
        {...noAutofillProps}
        {...textFieldProps}
      />
    );
  }

  // Fallback auf normales TextField wenn PlaceAutocompleteElement nicht verfügbar
  if (fallbackMode || !places) {
    return (
      <Box>
        {apiError && (
          <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
            {apiError} - Verwende einfaches Textfeld
          </Alert>
        )}
        <TextField
          label={label}
          value={value}
          onChange={onChange}
          error={error}
          helperText={helperText}
          required={required}
          disabled={disabled}
          fullWidth={fullWidth}
          placeholder={placeholder || 'Adresse eingeben...'}
          {...navProps}
          {...noAutofillProps}
          {...textFieldProps}
        />
      </Box>
    );
  }

  // `size="small"` wie bei MUI-Feldern: engeres Label. Die Höhe des Google-
  // Eingabefelds selbst gibt dessen Shadow DOM vor (48 px) und bleibt.
  const dense = textFieldProps.size === 'small';

  return (
    <Box>
      {/* Label */}
      {label && (
        <Box sx={{ mb: dense ? 0.25 : 1, lineHeight: dense ? 1.4 : undefined }}>
          <label style={{ fontSize: dense ? '0.75rem' : '0.875rem', color: error ? theme.palette.error.main : theme.palette.text.secondary }}>
            {label}
            {required && ' *'}
          </label>
        </Box>
      )}
      
      {/* Container für PlaceAutocompleteElement */}
      <Box
        ref={containerRef}
        sx={{
          '& gmp-place-autocomplete': {
            width: '100%',
            display: 'block',
            // Alle möglichen Input-Selektoren abdecken
            '& input, & input[type="text"], & input[type="search"], & .pac-input': {
              border: error ? '2px solid #d32f2f !important' : '1px solid rgba(0, 0, 0, 0.23) !important',
              borderRadius: '4px !important',
              padding: '14px !important',
              fontSize: '16px !important',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
              '&:focus': {
                borderColor: error ? '#d32f2f !important' : '#1976d2 !important',
              },
              '&:hover': {
                borderColor: error ? '#d32f2f !important' : 'rgba(0, 0, 0, 0.87) !important',
              }
            }
          }
        }}
      />
      
      
      {/* Unterziel-Auswahl. Tasten nicht an die Tastaturführung des Formulars
          durchreichen: Das Menü liegt im Portal, React-Events bubbeln aber
          trotzdem bis zu deren onKeyDown. */}
      <Menu
        open={!!subChoice}
        anchorEl={containerRef.current}
        onClose={cancelSubDestination}
        onKeyDown={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: containerRef.current?.offsetWidth || 280 } } }}
      >
        <ListSubheader sx={{ lineHeight: 1.4, py: 1 }}>
          {SUB_DESTINATION_HEADINGS[subDestinationRole] || 'Genauer auswählen'}
          {subChoice?.mainName ? ` · ${subChoice.mainName}` : ''}
        </ListSubheader>
        {(subChoice?.options || []).map((option, index) => (
          <MenuItem
            key={`${option.label}-${index}`}
            autoFocus={index === 0}
            onClick={() => chooseSubDestination(option)}
          >
            <ListItemIcon>{subDestinationIcon(option.label)}</ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>

      {needsLocation && unresolvedHint && (
        <Box sx={{ mt: 0.5, ml: 1.75, fontSize: '0.75rem', color: 'error.main' }}>
          Adresse nicht eindeutig – bitte einen Vorschlag aus der Liste wählen oder{' '}
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => setPinDialogOpen(true)}
            sx={{ verticalAlign: 'baseline' }}
          >
            auf Karte festlegen
          </Link>
        </Box>
      )}
      {requireLocation && (
        <MapPinDialog
          open={pinDialogOpen}
          address={value}
          locationBias={locationBias}
          onClose={() => setPinDialogOpen(false)}
          onConfirm={confirmMapPin}
        />
      )}

      {/* Helper Text */}
      {(helperText || error) && (
        <Box sx={{ mt: 0.5, ml: 1.75 }}>
          <span 
            style={{ 
              fontSize: '0.75rem', 
              color: error ? theme.palette.error.main : theme.palette.text.secondary
            }}
          >
            {helperText}
          </span>
        </Box>
      )}
    </Box>
  );
};

export default PlaceAutocompleteElementWrapper;